import { FeatureUnavailableComponent } from 'components/feature-unavailable'
import * as Flash from 'components/flash'
import { HeaderComponent } from 'components/header/component'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { Dataset } from 'generic/dataset'
import * as GenericRedux from 'generic/genericRedux'
import * as Utils from 'generic/utility'
import * as GA from 'google-analytics'
import { isEmpty } from 'lodash'
import React, {ComponentType, useState} from 'react'
import {Button, Input} from 'reactstrap'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { parseDate } from 'shared_server_client/dates'
import * as BillingPlan from 'shared_server_client/types/billing_plan'
import * as GlobalConstants from '../../constants'
import * as Constants from './constants'
import { DomainEmailSettingsModal } from './modals'
import { CopyButton, statusBanner} from './utils'
import { ConnectedComponentClass } from 'react-redux'
import {Fields} from "../../shared/modals"

export interface DomainConfiguration {
  email_configuration_id: number
  email_domain_id?: number  // ID of the email_domain record (if exists)
  configuration_name: string
  configuration_display_name?: string
  sender: string
  is_primary: boolean
  is_active: boolean
  dns_records: {
    mail_cname: { host: string, data: string }
    dkim1: { host: string, data: string }
    dkim2: { host: string, data: string }
    dmarc?: { host: string, data: string }
  } | null  // null if no domain record exists
  validation_status: 'verified' | 'invalid' | 'pending' | 'not_configured'
  last_validated?: string
  config?: any
  created: string
  updated: string
  has_domain_record: boolean  // indicates if domain record exists
  domain_id?: number  // only set if domain record exists
  can_generate_records: boolean  // indicates if user can generate DNS records
}

export interface Domain {
  domain_id: number
  domain: string
  account_id: number
  account_guid: string
  company_name?: string
  domain_status: string
  domain_last_verified?: string
  created: string
  updated: string
  configurations: DomainConfiguration[]
}

interface DomainListState {
  communityId: string,
  accountId: number,
  deleting: boolean,
  domains: Domain[],
  isAccountOwner: boolean,
  loaded: boolean,
  saving: boolean,
  validating: boolean,
  generating: boolean,  // for DNS record generation
  currentId: any,
  currentConfigId: any,
  emailLayoutId: any,
  plan?: BillingPlan.BillingPlan,
  availableConfigurations: any[],
  accountEmailConfigurations: any[],  // all email configurations for the account
  domainEmailConfigurations: any[],   // existing domain records
}

const unvalidatableDomains: string[] = [
  'gmail.com',
  'yahoo.com',
  'aol.com',
  'outlook.com',
]

export class SettingsDomainAuthentication extends React.Component<object, DomainListState> {

  public static contextType = RasaContext;
  private sharedStore: SharedStore
  constructor(props: any) {
    super(props)
    this.state = {
      communityId: '',
      accountId: 0,
      deleting: false,
      domains: [],
      isAccountOwner: false,
      loaded: false,
      saving: false,
      validating: false,
      generating: false,
      currentId: null,
      currentConfigId: null,
      emailLayoutId: null,
      availableConfigurations: [],
      accountEmailConfigurations: [],
      domainEmailConfigurations: [],
    }
  }

  public componentDidMount = () => {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
      this.sharedStore.getValue(SharedKeys.person),
    ])
    .then(([activeCommunity, person]) => {
      this.setState({
        communityId: activeCommunity.communityId,
        accountId: activeCommunity.communityAccountId,
        emailLayoutId: activeCommunity.communityInfo.data.email_layouts.filter((s: any) => s.is_active)[0].id,
        isAccountOwner: Utils.isAccountOwner(person, activeCommunity),
        plan: activeCommunity.billingInfo.currentPlan,
      }, () => {
        this.refreshRecords()
      })
    })
  }

  public render() {
    return (
      <div className="domain-auth">
        {this.state.loaded &&
        <div>
        {
          this.canAddDomains() ?
            <CreateDomainComponent create={this.createDomain} isSaving={this.state.saving}/>
            :
            <FeatureUnavailableComponent source={GA.UpgradeSource.DomainAuthentication}/>
        }
        </div>}
        {this.state.domains.length ?
        <div>
          <p>
            When you add a new domain, DNS records are automatically generated for you.
            Create ALL the CNAME records shown below in your DNS provider (usually where you purchased your domain).
            Once they are created, press the <strong>Validate</strong> button to check the status for each email configuration.
          </p>
          {this.state.domains.some(d => d.configurations && d.configurations.length > 1) && (
            <p>
              You may see multiple email configurations for each domain.  This provides redundance in your delivery infrastructure.
              Please ensure all records are entered.
            </p>
          )}
          <div className="instructions">
            <em>Please realize it can take up to four hours for the records to be properly propagated.</em>
            <br/>
            { GlobalConstants.RASA_HELP_HOW_TO_AUTH_DOMAIN &&
              <>
                Read <a href={GlobalConstants.RASA_HELP_HOW_TO_AUTH_DOMAIN}
                        target="_blank" rel="noopener">here</a> for more detailed instructions.
              </>
            }
          <hr/>
          </div>
          <div>
            {this.state.domains.map((d: Domain) => {
              return <EnhancedDomainsTable key={d.domain_id}
              domain={d}
              validateDomain={this.validateDomain}
              deleteDomain={this.deleteDomain}
              currentId={this.state.currentId}
              currentConfigId={this.state.currentConfigId}
              isValidating={this.state.validating}
              isDeleting={this.state.deleting}
              isGenerating={this.state.generating}
              generateDNSRecords={this.generateDNSRecords}
              isAccountOwner={this.state.isAccountOwner}/>
            })}
          </div>
        </div>
        : null}
      </div>
    )
  }

  private createDomain = (domain: string, openModal: any) => {
    this.setState({
      saving: true,
    }, () => {
      // Get all active email configurations for this account
      const activeConfigurations = this.state.accountEmailConfigurations.filter(config => config.is_active)

      if (activeConfigurations.length === 0) {
        this.context.store.dispatch(Flash.showFlashError('No active email configurations found. Please create an email configuration first.'))
        this.setState({ saving: false })
        return
      }

      // Create DNS records sequentially to avoid SendGrid rate limiting
      this.createDNSRecordsSequentially(activeConfigurations, domain, 0, [])
        .then((results) => {
          const successes = results.filter(r => r.success)
          const failures = results.filter(r => !r.success)

          const totalFailures = failures.length
          const totalSuccesses = successes.length

          if (totalSuccesses > 0 && totalFailures === 0) {
            // All succeeded
            this.context.store.dispatch(Flash.showFlashMessage(
              `DNS records generated for all ${totalSuccesses} email configuration${totalSuccesses > 1 ? 's' : ''}. Please add ALL DNS records to your domain provider.`
            ))
            openModal()
            this.refreshRecords()
          } else if (totalSuccesses > 0 && totalFailures > 0) {
            // Partial success
            const failedConfigs = failures.map(f => f.config.display_name || f.config.name).join(', ')

            this.context.store.dispatch(Flash.showFlashMessage(
              `DNS records generated for ${totalSuccesses} configuration${totalSuccesses > 1 ? 's' : ''}, but failed for: ${failedConfigs}. Please try again for the failed configurations.`
            ))
            openModal()
            this.refreshRecords()
          } else {
            // All failed
            this.context.store.dispatch(Flash.showFlashError(
              'Failed to generate DNS records for any email configuration. Please try again.'
            ))
          }

          this.setState({ saving: false })
        })
        .catch((error) => {
          this.context.store.dispatch(Flash.showFlashError('An unexpected error occurred while generating DNS records.'))
          this.setState({ saving: false })
        })
    })
  }

  private createDNSRecordsSequentially = async (configurations: any[], domain: string, index: number, results: any[]): Promise<any[]> => {
    if (index >= configurations.length) {
      return results
    }

    const config = configurations[index]

    try {
      const entityObject = await this.context.entityMetadata.getEntityObject('emailDomain')
      entityObject.setRecordId(this.state.communityId, null)
      entityObject.data.domain = domain
      entityObject.data.email_configuration_id = config.email_configuration_id

      const result = await entityObject.save()
      results.push({
        success: true,
        config,
        result,
      })
    } catch (error) {
      results.push({
        success: false,
        config,
        error,
      })
    }

    // Add a small delay between requests to be extra cautious with rate limiting
    await new Promise(resolve => setTimeout(resolve, 500))

    return this.createDNSRecordsSequentially(configurations, domain, index + 1, results)
  }

  private validateDomain = (domain: Domain) => {
    this.setState({
      validating: true,
      currentId: domain.domain_id,
      currentConfigId: null
    }, () => {
      // Validate all configurations that have DNS records
      const configurationsToValidate = domain.configurations.filter(config => config.has_domain_record)

      if (configurationsToValidate.length === 0) {
        this.context.store.dispatch(Flash.showFlashError('No configurations with DNS records found for validation'))
        this.setState({validating: false})
        return
      }

      // Validate each configuration sequentially
      this.validateConfigurationsSequentially(domain, configurationsToValidate, 0, [])
        .then((results) => {
          const successes = results.filter(r => r.success)
          const failures = results.filter(r => !r.success)

          if (successes.length > 0) {
            // Update validation status for successful configurations
            successes.forEach(result => {
              result.configuration.validation_status = 'verified'
              result.configuration.last_validated = new Date().toISOString()
            })
          }

          if (failures.length > 0) {
            // Update validation status for failed configurations
            failures.forEach(result => {
              result.configuration.validation_status = 'invalid'
            })
          }

          // Show appropriate message
          if (successes.length === configurationsToValidate.length) {
            this.context.store.dispatch(Flash.showFlashMessage(`All ${successes.length} configuration${successes.length > 1 ? 's' : ''} validated successfully`))
          } else if (successes.length > 0) {
            this.context.store.dispatch(Flash.showFlashMessage(`${successes.length} of ${configurationsToValidate.length} configurations validated successfully`))
          } else {
            this.context.store.dispatch(Flash.showFlashError('All configurations failed validation'))
          }

          this.setState({validating: false})
        })
        .catch((error) => {
          this.context.store.dispatch(Flash.showFlashError('An unexpected error occurred during validation'))
          this.setState({validating: false})
        })
    })
  }

  private validateConfigurationsSequentially = async (domain: Domain, configurations: DomainConfiguration[], index: number, results: any[]): Promise<any[]> => {
    if (index >= configurations.length) {
      return results
    }

    const configuration = configurations[index]

    try {
      const entityObject = await this.context.entityMetadata.getEntityObject('emailDomain')

      // Use email_domain_id if it exists (for existing DNS records)
      if (configuration.email_domain_id) {
        entityObject.setRecordId(this.state.communityId, configuration.email_domain_id)
      } else {
        entityObject.setRecordId(this.state.communityId, domain.domain_id)
      }

      // Add configuration context to validation
      entityObject.data.email_configuration_id = configuration.email_configuration_id

      const result = await entityObject.save()

      // Update email layout if needed
      try {
        const emailEntityObject = await this.context.entityMetadata.getEntityObject(
          'email_layout', this.state.communityId, this.state.emailLayoutId)
        const address = emailEntityObject._data.from_address
        if (address) {
          this.context.store.dispatch(({
            address,
            type: 'DOMAIN_VALIDATED',
          }))
        }
      } catch (emailError) {
        // Email layout update is optional, don't fail validation for this
      }

      results.push({
        success: true,
        configuration,
        result,
      })
    } catch (error) {
      results.push({
        success: false,
        configuration,
        error,
      })
    }

    // Add a small delay between validations
    await new Promise(resolve => setTimeout(resolve, 500))

    return this.validateConfigurationsSequentially(domain, configurations, index + 1, results)
  }

  private deleteDomain = (domainId: any) => {
    this.setState({deleting: true, currentId: domainId}, () => {
      this.context.entityMetadata.getEntityObject('emailDomain')
        .then((entityObject: any) => {
          entityObject.setRecordId(this.state.communityId, domainId)
          entityObject.archive().then((result) => {
            this.context.store.dispatch(Flash.showFlashMessage(Constants.DOMAIN_DELETE_SUCCESS))
            this.refreshRecords()
            this.setState({deleting: false})
          })
          .catch((error) => {
            this.context.store.dispatch(Flash.showFlashError(Constants.DOMAIN_DELETE_FAILURE))
            this.setState({deleting: false})
          })
        })
    })
  }

  private canAddDomains() {
    const maxDomainsAllowed = BillingPlan.getMaximumNumberOfDomainAuthentications(this.state.plan)
    return maxDomainsAllowed > 0 && maxDomainsAllowed > this.state.domains.length
  }

  private refreshRecords() {

    this.setState({
      domains: [],
      loaded: false,
    })

    // Load both datasets in parallel
    Promise.all([
      // Get all account email configurations
      new Dataset().loadCommunityDataset('accountEmailConfigurations', this.state.communityId, [{
        param: 'accountId',
        value: this.state.accountId
      }]),
      // Get existing domain email configurations (only those with domain records)
      new Dataset().loadCommunityDataset('domainEmailConfigurations', this.state.communityId, [{
        param: 'accountId',
        value: this.state.accountId
      }])
    ])
      .then(([accountConfigsResponse, domainConfigsResponse]) => {
        const accountConfigs = accountConfigsResponse[0] || []
        const domainConfigs = domainConfigsResponse[0] || []

        // Merge the data to create a complete picture
        const mergedDomains = this.mergeConfigurationData(accountConfigs, domainConfigs)


        this.setState({
          accountEmailConfigurations: accountConfigs,
          domainEmailConfigurations: domainConfigs,
          domains: mergedDomains,
          loaded: true,
        })
      })
      .catch((error) => {
        this.setState({
          domains: [],
          loaded: true,
        })
      })
  }



  private mergeConfigurationData = (accountConfigs: any[], domainConfigs: any[]): Domain[] => {
    // domainConfigs now contains flat records, each representing an email_domain record
    // We need to group these by domain and merge with account configurations
    const domainsMap = new Map<string, Domain>()

    // Process each flat domain configuration record
    domainConfigs.forEach(record => {
      if (!domainsMap.has(record.domain)) {
        // Create domain entry
        domainsMap.set(record.domain, {
          domain_id: record.domain_id,
          domain: record.domain,
          account_id: record.account_id,
          account_guid: record.account_guid,
          company_name: record.company_name,
          domain_status: record.domain_status,
          domain_last_verified: record.domain_last_verified,
          created: record.domain_created,
          updated: record.domain_updated,
          configurations: []
        })
      }

      const domain = domainsMap.get(record.domain)!

      // Add this configuration to the domain
      const configuration: DomainConfiguration = {
        email_configuration_id: record.email_configuration_id,
        email_domain_id: record.email_domain_id,  // Include email_domain record ID
        configuration_name: record.configuration_name,
        configuration_display_name: record.configuration_display_name,
        sender: record.sender,
        is_primary: record.is_primary,
        is_active: record.is_active,
        dns_records: record.dns_records,
        validation_status: record.validation_status,
        last_validated: record.domain_last_verified,
        config: record.config,
        created: record.configuration_created,
        updated: record.configuration_updated,
        has_domain_record: record.has_domain_record,
        domain_id: record.domain_id,
        can_generate_records: false  // These already have DNS records
      }

      domain.configurations.push(configuration)
    })

    // Now add configurations that don't have DNS records yet
    // For each unique domain from domainConfigs, add missing account configurations
    domainsMap.forEach(domain => {
      const existingConfigIds = domain.configurations.map(c => c.email_configuration_id)

      accountConfigs.forEach(accountConfig => {
        if (!existingConfigIds.includes(accountConfig.email_configuration_id)) {
          // This account configuration doesn't have DNS records for this domain yet
          const configuration: DomainConfiguration = {
            email_configuration_id: accountConfig.email_configuration_id,
            email_domain_id: undefined,  // No email_domain record exists yet
            configuration_name: accountConfig.name,
            configuration_display_name: accountConfig.display_name,
            sender: accountConfig.sender,
            is_primary: accountConfig.is_primary,
            is_active: accountConfig.is_active,
            dns_records: null,
            validation_status: 'not_configured',
            last_validated: undefined,
            config: accountConfig.config,
            created: accountConfig.created,
            updated: accountConfig.updated,
            has_domain_record: false,
            domain_id: domain.domain_id,
            can_generate_records: true  // Can generate since no record exists
          }

          domain.configurations.push(configuration)
        }
      })

      // Sort configurations within domain (primary first, then by creation date)
      domain.configurations.sort((a, b) => {
        if (a.is_primary && !b.is_primary) return -1
        if (!a.is_primary && b.is_primary) return 1
        return new Date(b.created).getTime() - new Date(a.created).getTime()
      })
    })

    return Array.from(domainsMap.values()).sort((a, b) => a.domain.localeCompare(b.domain))
  }

  private generateDNSRecords = (domain: Domain, configuration: DomainConfiguration) => {
    this.setState({
      generating: true,
      currentId: domain.domain_id,
      currentConfigId: configuration.email_configuration_id
    }, () => {
      this.context.entityMetadata.getEntityObject('emailDomain')
        .then((entityObject: any) => {
          // Create new domain record for this configuration
          entityObject.setRecordId(this.state.communityId, null)
          entityObject.data.domain = domain.domain
          entityObject.data.email_configuration_id = configuration.email_configuration_id

          entityObject.save().then((result) => {
            this.context.store.dispatch(Flash.showFlashMessage(`DNS records generated for ${configuration.sender.toUpperCase()} configuration`))
            this.refreshRecords()
            this.setState({
              generating: false,
            })
          })
          .catch((error) => {
            this.context.store.dispatch(Flash.showFlashError(`Failed to generate DNS records for ${configuration.sender.toUpperCase()} configuration`))
            this.setState({
              generating: false,
            })
          })
        })
    })
  }
}


type CreateDomainProps = GenericRedux.AllComponentPropsWithModal<any> & CreateDomainAdditionalProps
interface CreateDomainAdditionalProps {
  create: any,
  isSaving: boolean,
}

interface DomainAuthenticationState {
  domain: string,
  isSaving: boolean,
  validDomain: boolean,
}

const DataCell = ({host}) => {
  return <div className="domain-cell">
    <div className="cell-text">{host}</div>
    <CopyButton text={host}/>
  </div>
}

const HostCell = ({authHost, host}) => {
  return <DataCell host={host.replace(`.${authHost}`, '')}/>
}

const generateAllDNSRecordsText = (domain: Domain): string => {
  const records: string[] = []

  // Get all configurations that have DNS records
  const configurationsWithRecords = domain.configurations.filter(config => config.has_domain_record && config.dns_records)

  configurationsWithRecords.forEach(config => {
    const dns = config.dns_records
    const domainName = domain.domain

    // CNAME record for mail
    if (dns.mail_cname) {
      const hostName = dns.mail_cname.host.replace(`.${domainName}`, '')
      records.push(`CNAME\t${hostName}\t${dns.mail_cname.data}`)
    }

    // CNAME records for DKIM
    if (dns.dkim1) {
      const hostName = dns.dkim1.host.replace(`.${domainName}`, '')
      records.push(`CNAME\t${hostName}\t${dns.dkim1.data}`)
    }

    if (dns.dkim2) {
      const hostName = dns.dkim2.host.replace(`.${domainName}`, '')
      records.push(`CNAME\t${hostName}\t${dns.dkim2.data}`)
    }

    // TXT record for DMARC
    if (dns.dmarc) {
      records.push(`TXT\t${dns.dmarc.host}\t${dns.dmarc.data}`)
    }
  })

  return records.join('\n')
}

const CopyAllRecordsButton = ({ domain }: { domain: Domain }) => {
  const [showSuccess, setShowSuccess] = useState(false)

  const handleCopy = async () => {
    const recordsText = generateAllDNSRecordsText(domain)
    await Utils.copyToClipboard(recordsText)
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), 2000) // Hide success message after 2 seconds
  }

  return (
    <div
      className="copy-button"
      style={{ position: 'relative' }}
      onClick={handleCopy}
    >
      Copy All Records
      {showSuccess && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#28a745',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            marginTop: '4px',
            zIndex: 1000,
          }}
        >
          Copied to clipboard!
        </div>
      )}
    </div>
  )
}

export const DomainsTable = (props: any) =>
  <div className="domain-table">
    <div className="header-flex">
      <div>
        <p className="outer-text">
          Records for DNS host: &nbsp;
        </p>
      {props.d.domain}
      </div>
      <div className="newsletter ml-5">
        <p className="outer-text">
          Assigned to: &nbsp;
        </p>
        {props.d.community_name}
      </div>
      <div className="created">
        <p className="outer-text">
          Status: &nbsp;
        </p>
        <span style={{display: 'inline'}}>
          {statusBanner(props.d.status)}
        </span>
      </div>
    </div>
    <table className="actual-table">
      <thead>
            <tr>
              <td></td>
              <td>Type</td>
              <td>Name</td>
              <td>Value</td>
            </tr>
      </thead>
        <tbody>
            <tr>
              <td></td>
              <td>CNAME</td>
              <td><HostCell authHost={props.d.domain} host={props.d.dns_data.mail_cname.host}/></td>
              <td><DataCell host={props.d.dns_data.mail_cname.data}/></td>
            </tr>
            <tr>
              <td></td>
              <td>CNAME</td>
              <td><HostCell authHost={props.d.domain} host={props.d.dns_data.dkim1.host}/></td>
              <td><DataCell host={props.d.dns_data.dkim1.data}/></td>
            </tr>
            <tr>
              <td></td>
              <td>CNAME</td>
              <td><HostCell authHost={props.d.domain} host={props.d.dns_data.dkim2.host}/></td>
              <td><DataCell host={props.d.dns_data.dkim2.data}/></td>
            </tr>
            {renderDmarcRow(props)}
        </tbody>
    </table>
    <div className="validation-status">
      <div className="footer-text">
        <span>
          <p className="outer-text">
            Created:
          </p>
        &nbsp;
          {parseDate(props.d.created)}
        </span>
        <div>
          <p className="outer-text">
            Last Updated:
          </p>
          &nbsp;
          {parseDate(props.d.updated)}
        </div>
      </div>
      <div className="footer-button">
        {
        props.isAccountOwner ?
        <div>
        {props.isDeleting && props.currentId === props.d.id ?
        <Loading size="64" />
        :
        <Button className="delete-domain-btn" onClick={() =>  props.deleteDomain(props.d.id)}>
          Delete
        </Button>}
        </div>
        : null}
        {props.isValidating && props.currentId === props.d.id ?
        <Loading size="64" />
        :
        <Button onClick={() =>  props.validateDomain(props.d)}>
          Validate
        </Button>}
      </div>
    </div>
    <hr/>
  </div>

export const EnhancedDomainsTable = (props: any) => {
  const { domain } = props

  if (!domain.configurations || domain.configurations.length === 0) {
    return <div className="alert alert-warning">No configurations found for domain {domain.domain}</div>
  }

  return (
    <div className="domain-table">
      <div className="header-flex">
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <div>
            <p className="outer-text">
              Records for DNS host: &nbsp;
            </p>
            {domain.domain}
          </div>
          <CopyAllRecordsButton domain={domain} />
        </div>
        <div className="created">
          <p className="outer-text">
            Domain Created:
          </p>
          &nbsp;
          {parseDate(domain.created)}
        </div>
      </div>

      {/* Display each configuration as a separate section */}
      {domain.configurations.map((config: DomainConfiguration) => (
        <div key={config.email_configuration_id} className="configuration-section" style={{marginBottom: '20px', border: '1px solid #ddd', padding: '15px', borderRadius: '4px'}}>
          <div className="config-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <div>
              <h5 style={{margin: '0'}}>
                {config.configuration_display_name || config.configuration_name} ({config.sender.toUpperCase()})
                {config.is_primary ? <span className="badge badge-primary ml-2">PRIMARY</span> : ''}
              </h5>
            </div>
            <div>
              <span style={{display: 'inline'}}>
                {statusBanner(config.validation_status)}
              </span>
            </div>
          </div>

          {config.has_domain_record ? (
            <table className="actual-table">
              <thead>
                <tr>
                  <td></td>
                  <td>Type</td>
                  <td>Name</td>
                  <td>Value</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td></td>
                  <td>CNAME</td>
                  <td><HostCell authHost={domain.domain} host={config.dns_records.mail_cname.host}/></td>
                  <td><DataCell host={config.dns_records.mail_cname.data}/></td>
                </tr>
                <tr>
                  <td></td>
                  <td>CNAME</td>
                  <td><HostCell authHost={domain.domain} host={config.dns_records.dkim1.host}/></td>
                  <td><DataCell host={config.dns_records.dkim1.data}/></td>
                </tr>
                <tr>
                  <td></td>
                  <td>CNAME</td>
                  <td><HostCell authHost={domain.domain} host={config.dns_records.dkim2.host}/></td>
                  <td><DataCell host={config.dns_records.dkim2.data}/></td>
                </tr>
                {config.dns_records.dmarc && renderDmarcRowForConfig(config.dns_records.dmarc)}
              </tbody>
            </table>
          ) : (
            <div className="alert alert-warning" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px'}}>
              <div>
                <h6 style={{margin: '0 0 5px 0'}}>DNS Records Not Generated</h6>
                <p style={{margin: '0'}}>No DNS records have been generated for the <strong>{config.sender.toUpperCase()}</strong> email configuration.</p>
              </div>
              <div>
                {props.isGenerating && props.currentId === domain.domain_id && props.currentConfigId === config.email_configuration_id ? (
                  <Loading size="32" />
                ) : (
                  <Button
                    onClick={() => props.generateDNSRecords(domain, config)}
                    className="btn-primary"
                    size="lg"
                  >
                    Generate Records
                  </Button>
                )}
              </div>
            </div>
          )}

          {config.last_validated && (
            <div className="config-footer-text" style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
              Last Validated: {parseDate(config.last_validated)}
            </div>
          )}
        </div>
      ))}

      {/* Single validation and delete section for the entire domain */}
      <div className="validation-status">
        <div className="footer-button">
          {props.isAccountOwner ? (
            <div>
              {props.isDeleting && props.currentId === domain.domain_id ? (
                <Loading size="64" />
              ) : (
                <Button className="delete-domain-btn" onClick={() => props.deleteDomain(domain.domain_id)}>
                  Delete Domain
                </Button>
              )}
            </div>
          ) : null}
          {/* Single validate button for all configurations */}
          {props.isValidating && props.currentId === domain.domain_id ? (
            <Loading size="64" />
          ) : (
            <Button onClick={() => props.validateDomain(domain)}>
              Validate Domain
            </Button>
          )}
        </div>
      </div>
      <hr/>
    </div>
  )
}

const renderDmarcRowForConfig = (dmarcRecord) => {
  return <tr>
    <td>DMARC</td>
    <td>TXT</td>
    <td>{dmarcRecord.host}</td>
    <td>{dmarcRecord.data}</td>
  </tr>
}

const renderDmarcRow = (props) => {
  const hasValidDmarc = !!props.d.dmarc && !!props.d.dmarc.valid

  return <tr className={!hasValidDmarc ? 'dangerRow' : ''}>
      <td>DMARC</td>
      <td>TXT</td>
      <td>{hasValidDmarc ? props.d.dmarc.domain : 'Not Found'}</td>
      <td>{hasValidDmarc ? props.d.dmarc.dmarcRecord : GlobalConstants.RASA_HELP_DMARC && <a className="text-white" href={GlobalConstants.RASA_HELP_DMARC} target="_blank">Click here for more information</a>}</td>
    </tr>
}

const createDomainInitialState = {
  domain: '',
  isSaving: false,
  validDomain: true,
}
class CreateDomainComponentClass extends React.Component<CreateDomainProps, DomainAuthenticationState> {

  constructor(props: CreateDomainProps) {
    super(props)
    this.state = {
      domain: '',
      isSaving: false,
      validDomain: true,
    }
  }

  public render() {
    return (
      <div className="domain-auth">
        <HeaderComponent
          title={'Settings'}
          subTitle={'Domain Email'}
        />
        <DomainEmailSettingsModal data={this.props.modals}
          closeModal={this.props.closeModal}
          closeButtonText="OK"
          title="" />
        { GlobalConstants.RASA_HELP_OVERVIEW_OF_DOMAIN_AUTH &&
          <p className="intro-text">
          Verify a domain to use as a "from" address when you send emails.<br/>
          Read our <a href={GlobalConstants.RASA_HELP_OVERVIEW_OF_DOMAIN_AUTH} target="_blank" rel="noopener">
          send from email documentation </a>.
          </p>
        }
        <p className="intro-text">
          You'll need access to the DNS of this domain to be able to verify it.
        </p>
        <div className="input-flex">
          <span className="pre-input">WWW.</span>
          <Input  type="text"
                  name="domain"
                  placeholder="example.com"
                  invalid={!this.state.validDomain}
                  value={this.state.domain}
                  className="input"
                  id="domain"
                  onChange={(e) => this.updateDomain(e.target.value)}>
          </Input>
          {this.props.isSaving ?
          <div className="spinny">
            <Loading  size="64" />
          </div>
          :
          <Button className="auth-button"
                  disabled={!this.validForm() || this.state.isSaving}
                  onClick={this.createDomain}>
            Generate Records
          </Button>}
        </div>
        <hr/>
      </div>
    )
  }

  private createDomain = () => {
    this.props.create(this.state.domain, this.openModal)
    this.setState({domain: ''})
  }

  private openModal = () => {
    this.props.openModal(DomainEmailSettingsModal.key, {})
  }

  private validForm = () => {
    return this.state.validDomain && !isEmpty(this.state.domain)
  }

  private updateDomain = (domain: string) => {
    return this.setState({
      domain,
      validDomain: !isEmpty(domain) && this.isDomainValid(domain),
    })
  }

  private isDomainValid = (domain: string): boolean => {
    return Utils.validateDomain(domain) &&
           unvalidatableDomains.filter((d: string) => domain.endsWith(d)).length === 0
  }
}
export const CreateDomainComponent: ConnectedComponentClass<ComponentType<CreateDomainComponentClass>, Fields> = GenericRedux.registerNewComponentWithModals<any>(
  CreateDomainComponentClass,
  'domain_email_settings_modal',
  [DomainEmailSettingsModal.key],
  createDomainInitialState,
)
